import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import FlowerOfLife from "./FlowerOfLife";

const LAYERS = [
    {
        num: 1,
        name: "Entry-Level",
        price: "$14.99/mo",
        tagline: "Your first step into authentic networking.",
        detail:
            "Access to a safe space to build your network. Full onboarding and your 'Not A Goofball' badge. Weekly Roundtables, community forum access and monthly challenges.",
    },
    {
        num: 2,
        name: "Interest Circles",
        price: "Included",
        tagline: "Find your tribe by goal and industry.",
        detail:
            "Connect with like-minded professionals grouped by focus area. Smaller rooms, deeper conversations, warmer introductions.",
    },
    {
        num: 3,
        name: "Mentorship & Masterminds",
        price: "Unlocked",
        tagline: "Deeper support. Sharper strategy.",
        detail:
            "Access trusted community members for personal guidance, strategic development sessions and mastermind pods that keep you accountable.",
    },
    {
        num: 4,
        name: "Innovation Circles",
        price: "Invited",
        tagline: "Lead the ideas that move us forward.",
        detail:
            "A select group of forward-thinking members who drive initiatives, test new formats and shape what the community ships next.",
    },
    {
        num: 5,
        name: "Strategic Advisors",
        price: "Elected",
        tagline: "Guide the future direction.",
        detail:
            "Elite-level connections helping steer the community's long-term direction, and each other's biggest strategic decisions.",
    },
    {
        num: 6,
        name: "Zion",
        price: "Sacred",
        tagline: "Spiritual & legacy expansion.",
        detail:
            "Our innermost circle. A space for the most committed members to focus on legacy, purpose and the long game. Beyond business.",
    },
];

// Outer-to-inner ring radii (L1 outermost → L6 innermost/Zion)
const RADII = [280, 238, 196, 154, 112, 70];

// Gold palette shifting slightly per ring from cool gold outside to warm glow inside
const RING_COLORS = [
    "#d4af37", // L1 gold
    "#dcb842", // L2
    "#e6c150", // L3
    "#efca5e", // L4
    "#f2c652", // L5
    "#1ce5da", // L6 Zion — sacred cyan
];

const LayersOfOnion = () => {
    const [active, setActive] = useState(0);
    const current = LAYERS[active];
    const activeColor = RING_COLORS[active];
    const isZionActive = active === 5;

    return (
        <section
            id="layers"
            data-testid="layers-onion-visual"
            className="relative overflow-hidden bg-gradient-to-b from-[#0a0c27] via-[#0d1030] to-[#0a0c27]"
        >
            {/* Background Flower of Life — faint, rotating */}
            <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.15]"
                aria-hidden="true"
            >
                <div className="w-[140vmin] h-[140vmin] dn-rotate-slow">
                    <FlowerOfLife
                        color="#d4af37"
                        opacity={0.9}
                        strokeWidth={0.4}
                        className="w-full h-full"
                    />
                </div>
            </div>

            <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#f2c652] font-display mb-4">
                            III. Sacred Geometry of Growth
                        </div>
                        <h2 className="font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9] text-[#f6f1e1] max-w-3xl">
                            Grow with us, one{" "}
                            <span className="font-serif-italic font-light text-[#f2c652]">
                                layer
                            </span>{" "}
                            at a time.
                        </h2>
                    </div>
                    <p className="max-w-md text-[#f6f1e1]/60 font-body text-[15px] leading-relaxed">
                        Our community is built like sacred geometry. Each ring
                        inward requires more trust, more contribution, and more
                        presence. You enter at the outside. You earn your way
                        toward the center.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
                    {/* Concentric rings interactive visual */}
                    <div className="relative aspect-square max-w-[620px] w-full mx-auto hidden md:block">
                        <svg
                            viewBox="-320 -320 640 640"
                            className="w-full h-full dn-gold-glow"
                            aria-hidden="true"
                        >
                            <defs>
                                <radialGradient id="zion-core" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#1ce5da" stopOpacity="0.55" />
                                    <stop offset="45%" stopColor="#1ce5da" stopOpacity="0.15" />
                                    <stop offset="100%" stopColor="#1ce5da" stopOpacity="0" />
                                </radialGradient>
                                <radialGradient id="gold-core" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#fcc001" stopOpacity="0.75" />
                                    <stop offset="50%" stopColor="#fca112" stopOpacity="0.25" />
                                    <stop offset="100%" stopColor="#d4af37" stopOpacity="0" />
                                </radialGradient>
                                <filter id="ring-glow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feGaussianBlur stdDeviation="3" result="blur" />
                                    <feMerge>
                                        <feMergeNode in="blur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            {/* Outer cosmic halo */}
                            <circle
                                cx="0"
                                cy="0"
                                r="300"
                                fill="url(#gold-core)"
                                opacity="0.35"
                            />

                            {/* 6 concentric rings, outer to inner */}
                            {LAYERS.map((layer, i) => {
                                const r = RADII[i];
                                const isActive = i === active;
                                const isZion = layer.num === 6;
                                const color = RING_COLORS[i];
                                return (
                                    <g
                                        key={layer.num}
                                        onMouseEnter={() => setActive(i)}
                                        onClick={() => setActive(i)}
                                        className="cursor-pointer"
                                        data-testid={`onion-ring-${layer.num}`}
                                    >
                                        {/* Zion gets a filled cyan glow */}
                                        {isZion && (
                                            <circle
                                                cx="0"
                                                cy="0"
                                                r={r}
                                                fill="url(#zion-core)"
                                            />
                                        )}
                                        {/* The ring stroke */}
                                        <circle
                                            cx="0"
                                            cy="0"
                                            r={r}
                                            fill="none"
                                            stroke={color}
                                            strokeOpacity={isActive ? 1 : 0.45}
                                            strokeWidth={isActive ? 2 : 1}
                                            style={{ transition: "all 0.5s ease" }}
                                            filter={isActive ? "url(#ring-glow)" : undefined}
                                        />
                                        {/* Hit area (thick transparent stroke) */}
                                        <circle
                                            cx="0"
                                            cy="0"
                                            r={r}
                                            fill="transparent"
                                            stroke="transparent"
                                            strokeWidth="28"
                                            style={{ pointerEvents: "stroke" }}
                                        />
                                        {/* Label at top of ring */}
                                        <text
                                            x="0"
                                            y={-r + 5}
                                            textAnchor="middle"
                                            fill={
                                                isActive
                                                    ? color
                                                    : "rgba(246,241,225,0.45)"
                                            }
                                            fontFamily="Cabinet Grotesk, sans-serif"
                                            fontSize="13"
                                            fontWeight="700"
                                            letterSpacing="1.5"
                                            style={{ transition: "fill 0.4s ease" }}
                                        >
                                            L{layer.num}
                                        </text>
                                    </g>
                                );
                            })}

                            {/* Tiny sacred core dot */}
                            <circle cx="0" cy="0" r="4" fill="#1ce5da" />
                        </svg>

                        {/* Centered label overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                                <div
                                    className="font-display text-[10px] uppercase tracking-[0.35em] mb-1"
                                    style={{ color: activeColor }}
                                >
                                    Layer {current.num}
                                </div>
                                <div
                                    className="font-serif-italic font-light text-lg md:text-xl tracking-tight"
                                    style={{
                                        color: isZionActive
                                            ? "#1ce5da"
                                            : "#fcc001",
                                    }}
                                >
                                    {current.name}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detail panel + pills */}
                    <div>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={current.num}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -14 }}
                                transition={{ duration: 0.35 }}
                                className="hidden md:block border-l-2 pl-6"
                                style={{ borderColor: activeColor }}
                                data-testid="onion-detail-panel"
                            >
                                <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#f6f1e1]/50 font-display mb-4">
                                    <span>Layer {current.num}</span>
                                    <span className="h-px flex-1 bg-[#d4af37]/20" />
                                    <span style={{ color: activeColor }}>
                                        {current.price}
                                    </span>
                                </div>
                                <h3 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[#f6f1e1] mb-3 leading-tight">
                                    {current.name}
                                </h3>
                                <p className="text-[#f6f1e1]/80 font-body text-lg mb-4">
                                    {current.tagline}
                                </p>
                                <p className="text-[#f6f1e1]/55 font-body text-[15px] leading-relaxed">
                                    {current.detail}
                                </p>
                            </motion.div>
                        </AnimatePresence>

                        <div className="hidden md:flex flex-wrap gap-2 mt-8">
                            {LAYERS.map((l, i) => {
                                const color = RING_COLORS[i];
                                const isActive = i === active;
                                return (
                                    <button
                                        key={l.num}
                                        onClick={() => setActive(i)}
                                        data-testid={`onion-pill-${l.num}`}
                                        className={`font-display text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm border transition-all ${
                                            isActive
                                                ? "text-[#0a0c27]"
                                                : "border-[#d4af37]/25 text-[#f6f1e1]/60 hover:text-[#f6f1e1] hover:border-[#d4af37]/60"
                                        }`}
                                        style={
                                            isActive
                                                ? {
                                                      backgroundColor: color,
                                                      borderColor: color,
                                                  }
                                                : undefined
                                        }
                                    >
                                        L{l.num} · {l.name}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Mobile stacked list */}
                        <div className="md:hidden space-y-3">
                            {LAYERS.map((l, i) => {
                                const color = RING_COLORS[i];
                                const isActive = i === active;
                                const isZion = l.num === 6;
                                return (
                                    <motion.button
                                        key={l.num}
                                        initial={{ opacity: 0, y: 14 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{
                                            duration: 0.4,
                                            delay: i * 0.04,
                                        }}
                                        onClick={() => setActive(i)}
                                        data-testid={`onion-mobile-card-${l.num}`}
                                        className={`w-full text-left p-5 border rounded-sm transition-all ${
                                            isActive
                                                ? isZion
                                                    ? "border-[#1ce5da] bg-[#0e1e28]"
                                                    : "border-[#f2c652] bg-[#1a1730]"
                                                : "border-[#d4af37]/15 bg-[#0e1130]"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span
                                                className="font-display text-[10px] uppercase tracking-[0.3em]"
                                                style={{ color }}
                                            >
                                                Layer {l.num} · {l.price}
                                            </span>
                                            <ChevronRight
                                                className={`w-4 h-4 transition-transform ${
                                                    isActive
                                                        ? "rotate-90"
                                                        : "text-[#f6f1e1]/40"
                                                }`}
                                                style={
                                                    isActive
                                                        ? { color }
                                                        : undefined
                                                }
                                            />
                                        </div>
                                        <div className="font-display font-bold text-lg text-[#f6f1e1] mb-1">
                                            {l.name}
                                        </div>
                                        <p className="text-[#f6f1e1]/60 text-sm">
                                            {l.tagline}
                                        </p>
                                        {isActive && (
                                            <p className="text-[#f6f1e1]/55 text-sm mt-3 leading-relaxed">
                                                {l.detail}
                                            </p>
                                        )}
                                    </motion.button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LayersOfOnion;
